import classNames from 'classnames'; // Assuming classnames is available or can be added
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import './ChatButton.scss';

import { Icon } from 'ui';

const ChatButton = forwardRef(function ChatButton(
  {
    children = null,
    className = '',
    color = 'brand',
    disabled = false,
    icon = null,
    iconPosition = 'left',
    isActive = false,
    isFocusableWhenDisabled = false,
    onClick = () => {},
    size = 'small',
    variant = 'contained',
    ...rest
  },
  ref
) {
  const baseClassName = 'chat-button';
  const isAriaDisabled = disabled && isFocusableWhenDisabled;

  //   if variant is text or outlined, use the color of the button
  const iconColor = variant === 'text' || variant === 'outlined' ? color : 'white';

  const buttonClasses = classNames(
    baseClassName,
    `${baseClassName}--${variant}`,
    `${baseClassName}--${color}`,
    `${baseClassName}--${size}`,
    {
      [`${baseClassName}--active`]: isActive,
      [`${baseClassName}--icon-only`]: iconPosition === 'iconOnly',
      [`${baseClassName}--icon-left`]: icon && iconPosition === 'left' && iconPosition !== 'iconOnly',
      [`${baseClassName}--icon-right`]: icon && iconPosition === 'right' && iconPosition !== 'iconOnly',
      // Disabled styling comes from :disabled and [aria-disabled='true'] in the SCSS, not from a class
    },
    className
  );

  const renderIcon = () => {
    if (!icon) return null;
    // Assuming icon prop can be a string (icon name) or a node
    const iconSize = size === 'large' ? 'lg' : 'sm';
    return typeof icon === 'string' ? <Icon name={icon} className={`${baseClassName}__icon`} color={iconColor} size={iconSize} /> : icon;
  };

  const content =
    iconPosition === 'iconOnly' ? (
      renderIcon()
    ) : (
      <>
        {icon && iconPosition === 'left' && renderIcon()}
        {children && <span className={`${baseClassName}__text`}>{children}</span>}
        {icon && iconPosition === 'right' && renderIcon()}
      </>
    );

  function handleClick(event) {
    if (isAriaDisabled) return;
    onClick(event);
  }

  return (
    <button
      ref={ref}
      aria-disabled={isAriaDisabled || undefined}
      className={buttonClasses}
      disabled={disabled && !isFocusableWhenDisabled}
      onClick={handleClick}
      type="button" // Default to button type
      {...rest}
    >
      {content}
    </button>
  );
});

ChatButton.propTypes = {
  /** Button content (text or nodes) */
  children: PropTypes.node,
  /** Additional class names */
  className: PropTypes.string,
  /** Color theme of the button */
  color: PropTypes.oneOf(['brand']),
  /** Whether the button is disabled */
  disabled: PropTypes.bool,
  /** Icon element or name */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Position of the icon relative to the text */
  iconPosition: PropTypes.oneOf(['left', 'right', 'iconOnly']),
  /** Whether the button is in a persistent active/selected state */
  isActive: PropTypes.bool,
  /** Keeps a disabled button focusable (aria-disabled) so a tooltip can tell keyboard and screen reader users why */
  isFocusableWhenDisabled: PropTypes.bool,
  /** Click handler function */
  onClick: PropTypes.func,
  /** Size of the button */
  size: PropTypes.oneOf(['large', 'small']),
  /** Style variant of the button */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
};

export default ChatButton;
